import type { GridRowId, GridRowSelectionModel } from '@mui/x-data-grid-pro';

import type { ProjectArtifact } from '../../types';

/** Composite because one manifest can carry the same id under two kinds. */
export const getProjectArtifactRowId = (artifact: ProjectArtifact): string =>
  `${artifact.kind}-${artifact.id}`;

/**
 * Opt-in row selection for `ProjectArtifactsDataGrid`. Absent (the default) leaves
 * the grid unselectable and uncontrolled. The grid only reads the model.
 */
export interface ProjectArtifactsGridSelection {
  /** Whether rows can be picked. Passed even while false, so the model stays
   *  controlled across tab changes. */
  active: boolean;
  /** Selected row ids, keyed by `getProjectArtifactRowId`. */
  model: GridRowSelectionModel;
  /** The same ids, for membership lookups. */
  selectedRowIds: ReadonlySet<GridRowId>;
  toggleRow: (rowId: string, checked: boolean) => void;
  /** A refused row gets a disabled checkbox carrying `refusedReason` as a tooltip;
   *  a refusal with no reason renders no checkbox at all. */
  getRowSelectability: (artifact: ProjectArtifact) => {
    selectable: boolean;
    refusedReason?: string;
  };
  selectAll: {
    allSelected: boolean;
    /** Takes the listed pickable rows, truncated to the cap. Absent once they are
     *  all taken, and when nothing is pickable.
     *
     *  `displayedRowIds` is the grid's own row order — sorted and filtered as the
     *  user currently sees it. The cap makes select-all a "take the top N", and the
     *  top is whatever their sort put there, which the page-level artifact array
     *  does not know. An empty array falls back to that array's order. */
    apply?: (displayedRowIds: readonly GridRowId[]) => void;
  };
  /** Reached from the header only once everything is selected — the pill carries
   *  Clear below that. */
  clearAll: () => void;
}
